/**
 * JournalDetail — Life › Journal.
 * Private journal entries with mood, emotions, photos, location and NEXA
 * learning. Two views: list of entries (with mood trend) and the entry editor.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, ChevronLeft, Plus, Camera, MapPin, X, Check, Trash2, Sparkles, Image as ImageIcon,
  Clock, Locate, ArrowLeft, ArrowRight, Bold, Heading, List, Mic, Square, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi, photoUrl } from './api';

const MOODS = [
  { key: 'very_unpleasant', label: 'Very Unpleasant', valence: 10, color: '#8B5CF6' },
  { key: 'unpleasant',      label: 'Unpleasant',      valence: 30, color: '#6366F1' },
  { key: 'neutral',         label: 'Neutral',         valence: 50, color: '#64748B' },
  { key: 'pleasant',        label: 'Pleasant',        valence: 70, color: '#38BDF8' },
  { key: 'very_pleasant',   label: 'Very Pleasant',   valence: 90, color: '#22D3EE' },
];

// Ordered by Apple Journal's own catalogue — negative first, positive after.
const EMOTIONS = [
  'Angry', 'Anxious', 'Scared', 'Overwhelmed', 'Ashamed', 'Disgusted',
  'Embarrassed', 'Frustrated', 'Annoyed', 'Jealous', 'Stressed', 'Worried',
  'Guilty', 'Surprised', 'Hopeless', 'Irritated', 'Lonely', 'Discouraged',
  'Disappointed', 'Drained', 'Sad',
  'Calm', 'Confident', 'Content', 'Grateful', 'Hopeful', 'Excited',
  'Focused', 'Peaceful', 'Proud', 'Relaxed', 'Relieved', 'Loved', 'Amazed',
  'Amused', 'Happy', 'Inspired', 'Energetic', 'Curious', 'Passionate', 'Playful',
];

const fmtDay = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB',
      { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-GB',
      { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};
const moodBy = (key) => MOODS.find((m) => m.key === key) || MOODS[2];

/* Inline **bold** → <strong>. */
const mdInline = (text, kb) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (
    p.startsWith('**') && p.endsWith('**') && p.length > 4
      ? <strong key={`${kb}-${i}`} className="font-semibold text-white/85">{p.slice(2, -2)}</strong>
      : <span key={`${kb}-${i}`}>{p}</span>
  ));
};

/* Tiny markdown renderer — headings (##), bullets (-), bold (**). */
const MarkdownLite = ({ text, className = '' }) => {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} className="list-disc pl-4 space-y-0.5">{bullets}</ul>);
      bullets = [];
    }
  };
  lines.forEach((ln, i) => {
    if (/^\s*[-*]\s+/.test(ln)) {
      bullets.push(<li key={`li-${i}`}>{mdInline(ln.replace(/^\s*[-*]\s+/, ''), `li${i}`)}</li>);
    } else if (/^\s*##\s+/.test(ln)) {
      flush();
      blocks.push(
        <div key={`h-${i}`} className="font-semibold text-white/90 text-[1.05em] mt-1.5">
          {mdInline(ln.replace(/^\s*##\s+/, ''), `h${i}`)}
        </div>);
    } else if (ln.trim() === '') {
      flush();
    } else {
      flush();
      blocks.push(<p key={`p-${i}`}>{mdInline(ln, `p${i}`)}</p>);
    }
  });
  flush();
  return <div className={`space-y-1.5 ${className}`}>{blocks}</div>;
};

/* Small circular mood orb, echoes iOS Journal's petal blob. */
const MoodOrb = ({ mood, size = 48 }) => {
  const m = moodBy(mood);
  return (
    <div
      style={{ width: size, height: size, boxShadow: `0 0 ${size / 2}px ${m.color}44` }}
      className="rounded-full flex items-center justify-center relative overflow-hidden"
      aria-label={m.label}
    >
      <div
        className="absolute inset-0 rounded-full opacity-90"
        style={{ background: `radial-gradient(circle at 50% 45%, ${m.color} 0%, ${m.color}88 40%, transparent 75%)` }}
      />
      <div
        className="absolute rounded-full"
        style={{ width: size * 0.35, height: size * 0.35, background: m.color, filter: 'blur(0.5px)' }}
      />
    </div>
  );
};

/* --------------- Compress helper (matches wardrobe pattern) -------------- */
const _resize = (file, edge, q) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, edge / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL('image/jpeg', q).split(',')[1]);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});
const compressPhotos = async (files) => {
  const passes = [[1280, 0.82], [1024, 0.75], [820, 0.66], [720, 0.6]];
  let last = [];
  for (const [edge, q] of passes) {
    last = await Promise.all(files.map((f) => _resize(f, edge, q)));
    if (last.reduce((n, s) => n + s.length, 0) <= 2_800_000) return last;
  }
  return last;
};

/* -------------------------- Mood picker sheet ---------------------------- */
const MoodPicker = ({ initial, onDone, onClose }) => {
  const startIdx = MOODS.findIndex((m) => m.key === initial) || 2;
  const [idx, setIdx] = useState(startIdx >= 0 ? startIdx : 2);
  const [emotions, setEmotions] = useState([]);
  const [step, setStep] = useState(1); // 1=scale, 2=tags
  const m = MOODS[idx];
  const toggleE = (e) => setEmotions((es) => es.includes(e) ? es.filter((x) => x !== e) : [...es, e].slice(0, 12));
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="nexa-journal-mood-picker"
    >
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }}
        className="w-full max-w-md rounded-t-[28px] border-t border-x border-white/10 bg-[#0A0A0A] p-6 pb-8"
      >
        {step === 1 ? (
          <>
            <p className="text-center text-sm text-white/70">Choose how you&rsquo;re feeling right now</p>
            <div className="flex justify-center my-6" data-testid="nexa-journal-mood-orb">
              <MoodOrb mood={m.key} size={120} />
            </div>
            <div className="text-center text-xl font-light nexa-heading mb-5">{m.label}</div>
            <input
              type="range" min="0" max="4" step="1" value={idx}
              onChange={(e) => setIdx(Number(e.target.value))}
              data-testid="nexa-journal-mood-slider"
              className="w-full accent-sky-400"
            />
            <div className="flex justify-between text-[10px] font-mono tracking-[0.14em] text-white/45 uppercase mt-2">
              <span>Very unpleasant</span>
              <span>Very pleasant</span>
            </div>
            <button
              onClick={() => setStep(2)}
              data-testid="nexa-journal-mood-next"
              className="mt-7 w-full h-12 rounded-xl bg-sky-400 text-black font-semibold text-sm active:scale-[0.97] transition-transform"
            >
              Next — what best describes it?
            </button>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-3"><MoodOrb mood={m.key} size={72} /></div>
            <p className="text-center text-lg font-light nexa-heading mb-4">{m.label}</p>
            <p className="text-center text-xs text-white/50 mb-4">What best describes this feeling?</p>
            <div className="flex flex-wrap gap-2 justify-center max-h-64 overflow-y-auto pb-2">
              {EMOTIONS.map((e) => (
                <button key={e} onClick={() => toggleE(e)}
                  data-testid={`nexa-journal-emotion-${e.toLowerCase()}`}
                  className={`px-3 h-8 rounded-full text-xs transition-colors ${
                    emotions.includes(e)
                      ? 'bg-sky-400/20 border border-sky-400 text-sky-200'
                      : 'bg-white/[0.04] border border-white/10 text-white/70 hover:border-white/25'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
            <button
              onClick={() => onDone({ mood_label: m.key, mood_valence: m.valence, emotions })}
              data-testid="nexa-journal-mood-done"
              className="mt-5 w-full h-12 rounded-xl bg-sky-400 text-black font-semibold text-sm active:scale-[0.97] transition-transform"
            >
              Save mood
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ---------------------------- Entry editor ------------------------------- */
/* ------------------------------ Entry editor ---------------------------- */
const DRAFT_KEY = 'nexa_journal_draft_v2';

const toLocalInputValue = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const EntryEditor = ({ onSaved, onClose, journalType = 'life' }) => {
  const restored = (() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null') || {}; }
    catch { return {}; }
  })();

  const [title, setTitle] = useState(restored.title || '');
  const [body, setBody] = useState(restored.body || '');
  const [mood, setMood] = useState(restored.mood || { label: null, valence: null, emotions: [] });
  const [entryAt, setEntryAt] = useState(restored.entryAt || toLocalInputValue(new Date()));
  const [locationName, setLocationName] = useState(restored.locationName || '');
  const [location, setLocation] = useState(restored.location || null); // {lat, lng}
  const [photos, setPhotos] = useState([]); // Not persisted (File objects)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const bodyRef = useRef(null);
  const recorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const draftLoaded = useRef(false);

  // Show a gentle toast when a draft is restored
  useEffect(() => {
    if (draftLoaded.current) return;
    draftLoaded.current = true;
    if (restored.body || restored.title) {
      toast('Draft restored', { description: 'Your unsaved entry is back.' });
    }
  }, []);

  // Auto-save draft (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          title, body, mood, entryAt, locationName, location,
        }));
      } catch { /* quota – ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [title, body, mood, entryAt, locationName, location]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } };

  /* Insert markdown formatting around the current selection. */
  const applyFormat = (kind) => {
    const el = bodyRef.current;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    const sel = body.slice(start, end);
    let next = body;
    let caret = end;
    if (kind === 'bold') {
      const text = sel || 'bold';
      next = body.slice(0, start) + '**' + text + '**' + body.slice(end);
      caret = start + 2 + text.length + (sel ? 2 : 0);
    } else {
      const prefix = kind === 'heading' ? '## ' : '- ';
      const ls = body.lastIndexOf('\n', start - 1) + 1;
      next = body.slice(0, ls) + prefix + body.slice(ls);
      caret = end + prefix.length;
    }
    setBody(next);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } });
  };

  /* Insert plain text at the caret (used by voice transcription). */
  const insertText = (t) => {
    const el = bodyRef.current;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    const pre = body.slice(0, start);
    const glue = pre && !/\s$/.test(pre) ? ' ' : '';
    const next = pre + glue + t + body.slice(end);
    setBody(next);
    const caret = (pre + glue + t).length;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } });
  };

  /* Voice journaling — record → Whisper transcribe → insert at caret. */
  const toggleRecord = async () => {
    if (transcribing) return;
    if (recording) { recorderRef.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia) { toast.error('Voice input not supported here.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg'];
      const mime = mimes.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const b64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve(String(r.result).split(',')[1]);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          const CHUNK = 500 * 1024;
          const total = Math.max(1, Math.ceil(b64.length / CHUNK));
          const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          let data = null;
          for (let i = 0; i < total; i += 1) {
            const resp = await nexaApi.post('/chat/transcribe/chunk', {
              upload_id: uploadId, index: i, total,
              data: b64.slice(i * CHUNK, (i + 1) * CHUNK), mime: blob.type,
            });
            data = resp.data;
          }
          const t = (data.text || '').trim();
          if (t) insertText(t);
          else toast('No speech detected');
        } catch (err) {
          toast.error('Transcription failed: ' + (err.response?.data?.detail || err.message));
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError')
        toast.error('Microphone blocked. Enable it in Settings → Microphone.');
      else if (err?.name === 'NotFoundError') toast.error('No microphone found.');
      else toast.error(`Could not start recording (${err?.name || 'unknown'}).`);
    }
  };

  const wordCount = useMemo(() => {
    const t = body.trim();
    return t ? t.split(/\s+/).length : 0;
  }, [body]);
  const charCount = body.length;

  const entryDate = useMemo(() => {
    try { return new Date(entryAt); } catch { return new Date(); }
  }, [entryAt]);
  const dateIso = entryDate.toISOString();

  const hasDraft = Boolean(title || body || mood.label || photos.length || locationName);

  const addPhotos = (list) => {
    const files = Array.from(list || []);
    setPhotos((prev) => [
      ...prev,
      ...files.slice(0, 9 - prev.length).map((f) => ({
        id: `${Date.now()}-${Math.random()}`,
        file: f, url: URL.createObjectURL(f),
      })),
    ]);
  };

  const movePhoto = (idx, dir) => {
    setPhotos((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const removeEmotion = (e) =>
    setMood((m) => ({ ...m, emotions: (m.emotions || []).filter((x) => x !== e) }));

  const grabLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location not available on this device'); return;
    }
    toast('Getting your location…');
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      let place_name = '';
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=14`,
          { headers: { Accept: 'application/json' } });
        if (r.ok) {
          const j = await r.json();
          place_name = j.name || j.display_name?.split(',').slice(0, 2).join(', ') || '';
        }
      } catch { /* offline */ }
      setLocation({ lat, lng });
      setLocationName(place_name || `${lat.toFixed(3)}, ${lng.toFixed(3)}`);
      toast.success(place_name || `${lat.toFixed(3)}, ${lng.toFixed(3)}`);
    }, () => toast.error('Could not read your location'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
  };

  const submit = async () => {
    if (busy) return;
    if (!body.trim() && !title.trim()) {
      toast.error('Write something first'); return;
    }
    setBusy(true);
    try {
      const photos_b64 = photos.length
        ? (await compressPhotos(photos.map((p) => p.file))).map((b) => ({ image_base64: b }))
        : undefined;
      const finalLocation = locationName.trim() || location
        ? {
            lat: location?.lat ?? null,
            lng: location?.lng ?? null,
            place_name: locationName.trim() || null,
          }
        : null;
      await nexaApi.post('/journal', {
        title: title.trim() || null,
        body: body.trim(),
        journal_type: journalType,
        mood_label: mood.label,
        mood_valence: mood.valence,
        emotions: mood.emotions,
        photos: photos_b64,
        location: finalLocation,
        entry_at: entryDate.toISOString(),
      });
      toast.success('Saved — NEXA is reading it');
      clearDraft();
      onSaved();
      onClose();
    } catch {
      toast.error('Could not save the entry');
      setBusy(false);
    }
  };

  const askDiscard = () => {
    if (hasDraft) setConfirmDiscard(true);
    else onClose();
  };
  const doDiscard = () => { clearDraft(); onClose(); };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-[#0A0A0A] flex flex-col"
      data-testid="nexa-journal-editor"
    >
      {/* Header — back / date / save */}
      <div className="border-b border-white/8 shrink-0">
      <div className="flex items-center gap-2 px-4 py-3 max-w-2xl mx-auto w-full">
        <button onClick={askDiscard} data-testid="nexa-journal-editor-close" disabled={busy}
          className="w-9 h-9 shrink-0 rounded-full border border-white/15 flex items-center justify-center text-white/60 active:scale-95 transition-transform">
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </button>

        <button
          onClick={() => setDatePickerOpen((v) => !v)}
          data-testid="nexa-journal-date-toggle"
          className={`flex-1 min-w-0 h-9 px-3 rounded-full border flex items-center justify-center gap-2 text-[11px] tracking-[0.14em] uppercase font-mono transition-colors ${
            datePickerOpen ? 'border-sky-400/60 bg-sky-400/10 text-sky-200'
                           : 'border-white/12 text-white/70'
          }`}
        >
          <Clock className="w-3.5 h-3.5 text-sky-400 shrink-0" strokeWidth={2} />
          <span className="truncate">
            {fmtDay(dateIso)} · {fmtTime(dateIso)}
          </span>
        </button>

        <button onClick={submit} disabled={busy} data-testid="nexa-journal-editor-save"
          className="h-9 shrink-0 px-4 rounded-full bg-sky-400 text-black text-xs font-semibold tracking-wide disabled:opacity-40 flex items-center gap-1.5 active:scale-95 transition-transform">
          {busy ? (
            <>
              <div className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Saving
            </>
          ) : (
            <>
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
              Save
            </>
          )}
        </button>
      </div>
      </div>

      {/* Inline date/time picker (collapsible) */}
      <AnimatePresence initial={false}>
        {datePickerOpen && (
          <motion.div
            key="date-picker"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="border-b border-white/8 bg-white/[0.02] overflow-hidden"
          >
            <div className="px-4 py-3 flex items-center gap-2 max-w-2xl mx-auto w-full">
              <input
                type="datetime-local"
                value={entryAt}
                onChange={(e) => setEntryAt(e.target.value)}
                data-testid="nexa-journal-datetime-input"
                className="flex-1 min-w-0 bg-transparent text-white text-sm border border-white/15 rounded-lg px-3 py-2 outline-none focus:border-sky-400"
              />
              <button
                onClick={() => setEntryAt(toLocalInputValue(new Date()))}
                data-testid="nexa-journal-datetime-now"
                className="text-xs text-sky-300 border border-sky-400/40 rounded-lg px-3 py-2 active:scale-95 transition-transform"
              >
                Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mood row (prominent) */}
      <div className="px-5 pt-4 max-w-2xl mx-auto w-full shrink-0">
        <button
          onClick={() => setPickerOpen(true)}
          data-testid="nexa-journal-open-mood"
          className={`w-full h-12 rounded-2xl border flex items-center justify-center gap-3 text-sm transition-colors ${
            mood.label
              ? 'border-sky-400/40 bg-sky-400/[0.06] text-white'
              : 'border-white/10 bg-white/[0.02] text-white/60'
          }`}
        >
          {mood.label ? (
            <>
              <MoodOrb mood={mood.label} size={28} />
              <span className="font-medium">{moodBy(mood.label).label}</span>
              <span className="text-[11px] text-white/50 font-mono">
                {Math.round(mood.valence)}/100
              </span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 text-sky-400" strokeWidth={1.8} />
              <span>How were you feeling?</span>
            </>
          )}
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
      <div className="p-5 flex flex-col gap-4 max-w-2xl mx-auto w-full">
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          data-testid="nexa-journal-title"
          className="bg-transparent text-white text-2xl font-light nexa-heading placeholder-white/25 outline-none border-b border-white/10 pb-2"
        />
        {/* Formatting + voice toolbar */}
        <div className="flex items-center gap-1.5 -mb-1" data-testid="nexa-journal-format-toolbar">
          <button type="button" onClick={() => applyFormat('bold')} title="Bold (**text**)"
            data-testid="nexa-journal-fmt-bold"
            className="w-8 h-8 rounded-lg border border-white/12 flex items-center justify-center text-white/60 hover:text-sky-300 hover:border-sky-400/40 active:scale-95 transition">
            <Bold className="w-3.5 h-3.5" strokeWidth={2.4} />
          </button>
          <button type="button" onClick={() => applyFormat('heading')} title="Heading (## )"
            data-testid="nexa-journal-fmt-heading"
            className="w-8 h-8 rounded-lg border border-white/12 flex items-center justify-center text-white/60 hover:text-sky-300 hover:border-sky-400/40 active:scale-95 transition">
            <Heading className="w-3.5 h-3.5" strokeWidth={2.2} />
          </button>
          <button type="button" onClick={() => applyFormat('bullet')} title="Bullet list (- )"
            data-testid="nexa-journal-fmt-bullet"
            className="w-8 h-8 rounded-lg border border-white/12 flex items-center justify-center text-white/60 hover:text-sky-300 hover:border-sky-400/40 active:scale-95 transition">
            <List className="w-3.5 h-3.5" strokeWidth={2.2} />
          </button>
          <div className="flex-1" />
          {transcribing && (
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-sky-300/80 mr-1">
              Transcribing…
            </span>
          )}
          <button type="button" onClick={toggleRecord} disabled={transcribing}
            title={recording ? 'Stop & transcribe' : 'Dictate with voice'}
            data-testid="nexa-journal-mic"
            className={`h-8 px-3 rounded-lg border flex items-center gap-1.5 text-xs active:scale-95 transition ${
              recording ? 'border-rose-400/50 bg-rose-500/15 text-rose-200'
                        : 'border-sky-400/40 bg-sky-400/10 text-sky-200 hover:bg-sky-400/15'
            } disabled:opacity-50`}>
            {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.2} />
              : recording ? <Square className="w-3 h-3 fill-current" strokeWidth={2.4} />
              : <Mic className="w-3.5 h-3.5" strokeWidth={2} />}
            {recording ? 'Stop' : 'Voice'}
          </button>
        </div>
        <textarea
          ref={bodyRef}
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={journalType === 'dream'
            ? 'What did you dream about?  Use the toolbar for headings, bold and lists.'
            : 'What was today about?  Use the toolbar for headings, bold and lists.'}
          data-testid="nexa-journal-body"
          className="bg-transparent text-white/90 text-base leading-relaxed placeholder-white/25 outline-none min-h-[240px] resize-none"
          rows={12}
        />
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.15em] text-white/30 -mt-3">
          <span data-testid="nexa-journal-draft-status">
            {hasDraft ? 'Draft auto-saved' : 'Blank entry'}
          </span>
          <span data-testid="nexa-journal-wordcount">
            {wordCount} {wordCount === 1 ? 'word' : 'words'} · {charCount} chars
          </span>
        </div>

        {/* Emotion chips (tap to remove) */}
        {mood.emotions?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="nexa-journal-emotion-chips">
            {mood.emotions.map((e) => (
              <button
                key={e}
                onClick={() => removeEmotion(e)}
                data-testid={`nexa-journal-emotion-${e}`}
                className="group flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-sky-400/10 border border-sky-400/25 text-[11px] text-sky-200 active:scale-95 transition-transform"
                title="Tap to remove"
              >
                <span>{e}</span>
                <X className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" strokeWidth={3} />
              </button>
            ))}
          </div>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid="nexa-journal-photos">
            {photos.map((p, i) => (
              <div key={p.id}
                className="relative aspect-square rounded-xl overflow-hidden border border-white/10 group">
                <img src={p.url} alt=""
                  onClick={() => setViewPhoto(p.url)}
                  className="w-full h-full object-cover cursor-zoom-in" />
                <button
                  onClick={() => setPhotos((prev) => prev.filter((x) => x.id !== p.id))}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white/85"
                  aria-label="Remove photo"
                >
                  <X className="w-3 h-3" strokeWidth={3} />
                </button>
                {photos.length > 1 && (
                  <div className="absolute bottom-1 left-1 flex gap-1">
                    <button
                      onClick={() => movePhoto(i, -1)}
                      disabled={i === 0}
                      aria-label="Move photo left"
                      data-testid={`nexa-journal-photo-left-${i}`}
                      className="w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white/85 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={() => movePhoto(i, +1)}
                      disabled={i === photos.length - 1}
                      aria-label="Move photo right"
                      data-testid={`nexa-journal-photo-right-${i}`}
                      className="w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white/85 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Location — manual text + GPS */}
        <div className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2"
             data-testid="nexa-journal-location">
          <MapPin className="w-3.5 h-3.5 text-sky-400 shrink-0" />
          <input
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            placeholder="Where are you? (optional)"
            data-testid="nexa-journal-location-input"
            className="flex-1 min-w-0 bg-transparent text-white/90 text-sm placeholder-white/30 outline-none"
          />
          <button
            onClick={grabLocation}
            data-testid="nexa-journal-location-gps"
            title="Use my current location"
            className="text-sky-300/80 hover:text-sky-300 active:scale-90 transition-transform"
          >
            <Locate className="w-4 h-4" strokeWidth={1.8} />
          </button>
          {(locationName || location) && (
            <button
              onClick={() => { setLocationName(''); setLocation(null); }}
              data-testid="nexa-journal-location-clear"
              className="text-white/40 hover:text-white/70"
              aria-label="Clear location"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Toolbar */}
      <div className="border-t border-white/8 shrink-0">
      <div className="px-4 py-3 flex items-center gap-3 max-w-2xl mx-auto w-full">
        <label data-testid="nexa-journal-add-photos"
          className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-sky-300 cursor-pointer active:scale-95 transition-transform">
          <ImageIcon className="w-4 h-4" strokeWidth={1.8} />
          <input type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
        </label>
        <label data-testid="nexa-journal-add-camera"
          className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-sky-300 cursor-pointer active:scale-95 transition-transform">
          <Camera className="w-4 h-4" strokeWidth={1.8} />
          <input type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
        </label>
        <div className="flex-1" />
        <button
          onClick={askDiscard}
          disabled={busy}
          data-testid="nexa-journal-discard"
          className="h-9 px-3 rounded-full border border-white/15 text-white/60 text-xs active:scale-95 transition-transform hover:border-white/25"
        >
          Discard
        </button>
      </div>
      </div>

      {/* Mood picker */}
      <AnimatePresence>
        {pickerOpen && (
          <MoodPicker
            initial={mood.label}
            onDone={(m) => { setMood({ label: m.mood_label, valence: m.mood_valence, emotions: m.emotions }); setPickerOpen(false); }}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Photo viewer */}
      <AnimatePresence>
        {viewPhoto && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setViewPhoto(null)}
            data-testid="nexa-journal-photo-viewer"
          >
            <img src={viewPhoto} alt="" className="max-w-full max-h-full object-contain" />
            <button
              onClick={() => setViewPhoto(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white"
              aria-label="Close photo"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Discard confirm */}
      <AnimatePresence>
        {confirmDiscard && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="nexa-journal-discard-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="w-full max-w-sm rounded-2xl border border-white/12 bg-[#0F0F10] p-5"
            >
              <div className="text-white/90 text-base font-medium">Discard this entry?</div>
              <div className="text-white/50 text-xs mt-1.5 leading-relaxed">
                Your draft is auto-saved as you type. Discard removes it — this can&apos;t be undone.
              </div>
              <div className="mt-5 flex items-center gap-2">
                <button
                  onClick={() => setConfirmDiscard(false)}
                  data-testid="nexa-journal-discard-cancel"
                  className="flex-1 h-10 rounded-xl border border-white/15 text-white/80 text-sm active:scale-95 transition-transform"
                >
                  Keep draft
                </button>
                <button
                  onClick={doDiscard}
                  data-testid="nexa-journal-discard-confirm"
                  className="flex-1 h-10 rounded-xl bg-red-500/90 text-white text-sm font-medium active:scale-95 transition-transform"
                >
                  Discard
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>,
    document.body
  );
};

/* -------------------------- Entry row (list) ----------------------------- */
const EntryRow = ({ entry, onDelete }) => {
  const m = moodBy(entry.mood_label);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4 flex gap-3"
         data-testid={`nexa-journal-entry-${entry.id}`}>
      <div className="shrink-0"><MoodOrb mood={entry.mood_label} size={44} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[13px] text-white/90 font-medium truncate">
            {entry.title || (entry.body || '').replace(/[*#>-]/g, '').trim().slice(0, 60)}
          </div>
          <div className="shrink-0 text-[10px] font-mono text-white/40">
            {fmtTime(entry.entry_at)}
          </div>
        </div>
        <div className="text-[10px] font-mono tracking-[0.15em] uppercase text-white/40 mt-0.5">
          {fmtDay(entry.entry_at)} · {m.label}
        </div>
        {entry.body && (
          <div className="text-[12px] text-white/60 mt-2 leading-relaxed break-words max-h-[4.6rem] overflow-hidden [mask-image:linear-gradient(to_bottom,black_55%,transparent)]">
            <MarkdownLite text={entry.body} />
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {(entry.emotions || []).slice(0, 4).map((e) => (
            <span key={e} className="px-2 h-5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] text-white/60 flex items-center">
              {e}
            </span>
          ))}
          {entry.location?.place_name && (
            <span className="flex items-center gap-1 text-[10px] text-sky-300/80">
              <MapPin className="w-3 h-3" /> {entry.location.place_name}
            </span>
          )}
          {(entry.photo_ids || []).length > 0 && (
            <span className="text-[10px] text-white/50 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> {entry.photo_ids.length}
            </span>
          )}
        </div>
        {(entry.photo_ids || []).length > 0 && (
          <div className="mt-2 flex gap-1.5 overflow-x-auto -mx-1 px-1">
            {entry.photo_ids.slice(0, 4).map((pid) => (
              <img key={pid} src={photoUrl(pid)} alt=""
                loading="lazy"
                className="w-16 h-16 rounded-lg object-cover border border-white/10 shrink-0" />
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => { if (window.confirm('Delete this entry?')) onDelete(entry.id); }}
        aria-label="Delete entry"
        data-testid={`nexa-journal-delete-${entry.id}`}
        className="self-start text-white/25 hover:text-rose-400 p-1"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

/* -------------------------- Main view ------------------------------------ */
export const JournalDetail = () => {
  const [entries, setEntries] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [jType, setJType] = useState('life');

  const load = () => nexaApi.get('/journal', { params: { journal_type: jType } })
    .then(({ data }) => setEntries(data.entries || []))
    .catch(() => setEntries([]));
  useEffect(() => { setEntries(null); load(); /* eslint-disable-next-line */ }, [jType]);

  const del = async (id) => {
    setEntries((prev) => prev ? prev.filter((e) => e.id !== id) : prev);
    try { await nexaApi.delete(`/journal/${id}`); }
    catch { toast.error('Could not delete'); load(); }
  };

  const trend = useMemo(() => {
    if (!entries || !entries.length) return null;
    const recent = entries.slice(0, 14);
    const avg = recent.reduce((n, e) => n + (e.mood_valence || 50), 0) / recent.length;
    return { avg, count: recent.length };
  }, [entries]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-4" data-testid="nexa-journal-view"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/80 uppercase mb-1.5 flex items-center gap-1.5">
            <BookOpen className="w-3 h-3" /> Journal
          </div>
          <p className="text-white/50 text-sm">
            {entries === null ? 'Loading…' : entries.length === 0
              ? (jType === 'dream'
                  ? 'Record your dreams the moment you wake.'
                  : 'Start writing — NEXA learns from every entry.')
              : 'Private. NEXA reads and updates your personality profile.'}
          </p>
        </div>
        <button
          onClick={() => setEditorOpen(true)}
          data-testid="nexa-journal-new-entry"
          className="w-11 h-11 rounded-full bg-sky-400 text-black flex items-center justify-center active:scale-95 transition-transform"
          aria-label="New entry"
        >
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {/* Life / Dreams switch */}
      <div className="flex items-center gap-1 p-1 rounded-2xl border border-white/10 bg-white/[0.03]"
           data-testid="nexa-journal-tabs">
        {[['life', 'Life'], ['dream', 'Dreams']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setJType(key)}
            data-testid={`nexa-journal-tab-${key}`}
            className={`flex-1 h-9 rounded-xl text-sm font-medium transition-colors ${
              jType === key ? 'bg-sky-400 text-black' : 'text-white/60 hover:text-white/85'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {trend && (
        <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4 flex items-center gap-4"
             data-testid="nexa-journal-mood-trend">
          <MoodOrb
            mood={
              trend.avg > 80 ? 'very_pleasant'
              : trend.avg > 60 ? 'pleasant'
              : trend.avg > 40 ? 'neutral'
              : trend.avg > 20 ? 'unpleasant'
              : 'very_unpleasant'
            }
            size={54}
          />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono tracking-[0.2em] text-white/40 uppercase">Mood trend</div>
            <div className="nexa-heading text-2xl font-light mt-0.5">{Math.round(trend.avg)}<span className="text-sm text-white/40">/100</span></div>
            <div className="text-[11px] text-white/50 font-mono">avg over last {trend.count} entries</div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {entries === null && (
          <div className="text-sm text-white/40">Loading…</div>
        )}
        {entries !== null && entries.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-white/45">
            {jType === 'dream'
              ? <>No dreams recorded yet. Tap <span className="text-sky-400">+</span> after you wake.</>
              : <>No entries yet. Tap <span className="text-sky-400">+</span> to write one.</>}
          </div>
        )}
        {(entries || []).map((e) => (
          <EntryRow key={e.id} entry={e} onDelete={del} />
        ))}
      </div>

      <AnimatePresence>
        {editorOpen && (
          <EntryEditor journalType={jType} onSaved={load} onClose={() => setEditorOpen(false)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default JournalDetail;
